import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { ArrowLeft, Upload, Trash2, BookOpen } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminPage() {
  const navigate = useNavigate();
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    author: '',
    description: '',
    page_count: '',
    featured: false,
    price: '',
    cover: null,
    content: null
  });

  useEffect(() => {
    fetchBooks();
  }, []);

  const fetchBooks = async () => {
    try {
      const response = await axios.get(`${API}/books`);
      setBooks(response.data);
    } catch (error) {
      console.error('Failed to fetch books:', error);
    }
  };

  const handleSeedSampleBooks = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/seed-sample-books`);
      toast.success(response.data.message);
      fetchBooks();
    } catch (error) {
      toast.error('Failed to seed sample books');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.cover || !formData.content) {
      toast.error('Please select both cover image and content file');
      return;
    }

    setUploading(true);

    const data = new FormData();
    data.append('title', formData.title);
    data.append('author', formData.author);
    data.append('description', formData.description);
    data.append('page_count', formData.page_count);
    data.append('featured', formData.featured);
    if (formData.price) {
      data.append('price', formData.price);
    }
    data.append('cover', formData.cover);
    data.append('content', formData.content);

    try {
      await axios.post(`${API}/books`, data, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Book uploaded successfully!');

      // Reset form
      setFormData({
        title: '',
        author: '',
        description: '',
        page_count: '',
        featured: false,
        price: '',
        cover: null,
        content: null
      });

      // Reset file inputs
      document.getElementById('cover-input').value = '';
      document.getElementById('content-input').value = '';

      fetchBooks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload book');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (bookId) => {
    if (!window.confirm('Are you sure you want to delete this book?')) {
      return;
    }

    try {
      await axios.delete(`${API}/books/${bookId}`);
      toast.success('Book deleted successfully');
      fetchBooks();
    } catch (error) {
      toast.error('Failed to delete book');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#E8F4F8] via-[#F5F1E8] to-[#D8F3F0]">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-[#0CAAA6]/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="text-[#0CAAA6]"
              data-testid="back-button"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Island
            </Button>
            <h1 className="text-2xl font-bold text-[#1A4D5C]">🏝️ Admin Panel</h1>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Upload Form */}
          <div>
            <Card className="p-6 border-[#0CAAA6]/20">
              <h2 className="text-2xl font-bold text-[#1A4D5C] mb-6 flex items-center gap-2">
                <Upload className="w-6 h-6" />
                Upload New Book
              </h2>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Title *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="The Island Chronicles"
                    required
                    data-testid="title-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="author">Author *</Label>
                  <Input
                    id="author"
                    value={formData.author}
                    onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                    placeholder="Ky4Kings"
                    required
                    data-testid="author-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description *</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="A captivating journey through..."
                    rows={4}
                    required
                    data-testid="description-input"
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="page_count">Page Count *</Label>
                    <Input
                      id="page_count"
                      type="number"
                      value={formData.page_count}
                      onChange={(e) => setFormData({ ...formData, page_count: e.target.value })}
                      placeholder="245"
                      required
                      data-testid="page-count-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="price">Price ($) - Optional</Label>
                    <Input
                      id="price"
                      type="number"
                      step="0.01"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      placeholder="4.99"
                      data-testid="price-input"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Switch
                    id="featured"
                    checked={formData.featured}
                    onCheckedChange={(checked) => setFormData({ ...formData, featured: checked })}
                    data-testid="featured-switch"
                  />
                  <Label htmlFor="featured" className="cursor-pointer">
                    Featured Book
                  </Label>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cover-input">Cover Image (JPG, PNG, WebP) *</Label>
                  <Input
                    id="cover-input"
                    type="file"
                    accept=".jpg,.jpeg,.png,.webp"
                    onChange={(e) => setFormData({ ...formData, cover: e.target.files[0] })}
                    required
                    data-testid="cover-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="content-input">Book Content (PDF or EPUB) *</Label>
                  <Input
                    id="content-input"
                    type="file"
                    accept=".pdf,.epub"
                    onChange={(e) => setFormData({ ...formData, content: e.target.files[0] })}
                    required
                    data-testid="content-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={uploading}
                  className="w-full bg-[#0CAAA6] hover:bg-[#0A8883] text-white h-12"
                  data-testid="upload-button"
                >
                  {uploading ? 'Uploading...' : 'Upload Book'}
                </Button>
              </form>

              <div className="mt-6 pt-6 border-t border-[#0CAAA6]/20">
                <Button
                  onClick={handleSeedSampleBooks}
                  disabled={loading}
                  variant="outline"
                  className="w-full border-[#0CAAA6] text-[#0CAAA6]"
                  data-testid="seed-button"
                >
                  {loading ? 'Seeding...' : 'Seed Sample Books (Demo)'}
                </Button>
              </div>
            </Card>
          </div>

          {/* Books List */}
          <div>
            <Card className="p-6 border-[#0CAAA6]/20">
              <h2 className="text-2xl font-bold text-[#1A4D5C] mb-6 flex items-center gap-2">
                <BookOpen className="w-6 h-6" />
                Uploaded Books ({books.length})
              </h2>

              {books.length === 0 ? (
                <div className="text-center py-12">
                  <span className="text-6xl block mb-4">🏝️</span>
                  <p className="text-[#7A8286]">No books yet. Upload your first one!</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                  {books.map((book) => (
                    <div
                      key={book.id}
                      className="flex items-center gap-3 p-3 bg-[#F5F1E8] rounded-lg hover:bg-[#F5F1E8]/70 transition-colors"
                      data-testid={`book-item-${book.id}`}
                    >
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}${book.cover_url}`}
                        alt={book.title}
                        className="w-16 h-20 object-cover rounded"
                      />
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-[#1A4D5C] truncate">{book.title}</h4>
                        <p className="text-sm text-[#7A8286]">{book.author}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-[#7A8286]">{book.read_count} reads</span>
                          {book.featured && (
                            <span className="text-xs bg-[#0CAAA6]/20 text-[#0CAAA6] px-2 py-0.5 rounded-full">
                              Featured
                            </span>
                          )}
                          {book.price && (
                            <span className="text-xs bg-[#FF6B6B]/20 text-[#FF6B6B] px-2 py-0.5 rounded-full">
                              ${book.price}
                            </span>
                          )}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(book.id)}
                        className="text-[#FF6B6B] hover:text-[#FF6B6B] hover:bg-[#FF6B6B]/10"
                        data-testid={`delete-button-${book.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}